import { controlApiOrigin, controlLoginUrl } from './control-origin';

/**
 * Hand-written client for control-api's `/api/admin` and `/api/v1/me` surfaces.
 *
 * This deliberately does not live in `src/adapters/`: those clients are
 * generated wholesale from mesh's own OpenAPI specs, and control-api publishes
 * no spec for these routes. They also send `credentials: 'same-origin'` and
 * bounce a 401 to mesh's `/login`, both of which are wrong here.
 */

/**
 * `PATCH` is absent on purpose: it fails control-api's CORS preflight, so a
 * caller must not be able to reach for it.
 */
export type ControlApiMethod = 'GET' | 'POST' | 'PUT' | 'DELETE';

/**
 * The four failure envelopes control-api can return. Parsing has to be
 * defensive because they do not share a field: an RFC 9457 body has no `error`,
 * so `body.error` is `undefined` on the most common failure of all.
 */
export type ControlApiErrorKind =
  /** RFC 9457 `{type, title, status, detail, instance?}` — the normal failure. */
  | 'problem'
  /** `{"error": "..."}` — the 503 "not configured" paths. */
  | 'error-field'
  /** `{"error": "session_expired", "message": "..."}` — token-version middleware. */
  | 'session-expired'
  /** No JSON at all. `405` returns only a status and an `Allow` header. */
  | 'empty';

export class ControlApiError extends Error {
  readonly status: number;
  readonly kind: ControlApiErrorKind;
  /**
   * The machine-readable `error` value when the body carried one, e.g.
   * `mesh_outbox_unavailable`. Lets a caller recognise a specific condition
   * without matching on prose, which is what `message` is for.
   */
  readonly code: string | null;

  constructor(
    status: number,
    kind: ControlApiErrorKind,
    message: string,
    code: string | null = null
  ) {
    super(message);
    this.name = 'ControlApiError';
    this.status = status;
    this.kind = kind;
    this.code = code;
  }

  /** True when the user has no usable Control session and must log in again. */
  get isUnauthenticated(): boolean {
    return this.status === 401;
  }
}

/** Raised when the Mesh UI cannot work out which control-api to talk to. */
export class ControlOriginUnknownError extends Error {
  constructor() {
    super(
      'Could not derive control-api origin from this host. Set VITE_CONTROL_API_ORIGIN for local development.'
    );
    this.name = 'ControlOriginUnknownError';
  }
}

type ProblemBody = {
  title?: unknown;
  detail?: unknown;
  error?: unknown;
  message?: unknown;
};

function describe(
  status: number,
  body: ProblemBody | null
): {
  kind: ControlApiErrorKind;
  message: string;
  code: string | null;
} {
  if (body === null) {
    return {
      kind: 'empty',
      message: `Request failed with status ${status}`,
      code: null,
    };
  }

  const detail =
    typeof body.detail === 'string' && body.detail !== '' ? body.detail : null;
  const title =
    typeof body.title === 'string' && body.title !== '' ? body.title : null;

  if (typeof body.error === 'string') {
    if (body.error === 'session_expired') {
      const expired =
        typeof body.message === 'string' && body.message !== ''
          ? body.message
          : 'Your session has expired. Sign in again to continue.';
      return {
        kind: 'session-expired',
        message: expired,
        code: body.error,
      };
    }

    // Prefer `detail` for the message where a body carries both. Several
    // endpoints answer `{error: "<machine_code>", detail: "<what to do>"}`,
    // and surfacing the code alone threw away the half a human can act on.
    // The code is still available, typed, on `ControlApiError.code`.
    return {
      kind: 'error-field',
      message: detail ?? body.error,
      code: body.error,
    };
  }

  const message = detail ?? title;

  if (message) {
    return { kind: 'problem', message, code: null };
  }

  return {
    kind: 'empty',
    message: `Request failed with status ${status}`,
    code: null,
  };
}

async function readBody(res: Response): Promise<ProblemBody | null> {
  const text = await res.text();
  if (text.trim() === '') {
    return null;
  }
  try {
    return JSON.parse(text) as ProblemBody;
  } catch {
    return null;
  }
}

export type ControlApiRequest = {
  method?: ControlApiMethod;
  /** Path beginning with `/`, e.g. `/api/admin/tenants`. */
  path: string;
  query?: Record<string, string | number | boolean | undefined>;
  body?: unknown;
  signal?: AbortSignal;
};

function buildUrl(
  origin: string,
  path: string,
  query: ControlApiRequest['query']
): string {
  const url = new URL(path, origin);
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value !== undefined) {
      url.searchParams.set(key, String(value));
    }
  }
  return url.toString();
}

/**
 * Issue a request to control-api with the session cookie attached.
 *
 * `credentials: 'include'` is the whole authentication story — no
 * `Authorization` header, no token in JavaScript. The browser attaches
 * Control's cookie because the two hosts are same-site.
 */
export async function controlApiRequest<T>({
  method = 'GET',
  path,
  query,
  body,
  signal,
}: ControlApiRequest): Promise<T> {
  const origin = controlApiOrigin();
  if (!origin) {
    throw new ControlOriginUnknownError();
  }

  const res = await fetch(buildUrl(origin, path, query), {
    method,
    credentials: 'include',
    signal,
    headers:
      body === undefined ? undefined : { 'Content-Type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  if (!res.ok) {
    const { kind, message, code } = describe(res.status, await readBody(res));
    throw new ControlApiError(res.status, kind, message, code);
  }

  if (res.status === 204) {
    return undefined as T;
  }

  const parsed = await readBody(res);
  return parsed as T;
}

/**
 * Hand the browser to Control's login page, coming back here afterwards.
 * Returns `false` when the control host could not be derived, so a caller can
 * surface an error rather than appear to hang.
 */
export function redirectToControlLogin(): boolean {
  const url = controlLoginUrl();
  if (!url) {
    return false;
  }
  window.location.assign(url);
  return true;
}
